import { Dispatch, SetStateAction, useState, useEffect, useCallback, useRef } from 'react';
import s from './index.module.scss';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { generateSignature } from '@/util/signature';
import { Stage } from '../const';
import { useToast } from '@/stores/ToastContext';
import { COUNTRY_CODES } from './const';
import HCaptcha from '@/component/hCaptcha/h5Index';
import { OpenCAPTCHA } from '@/component/login/logEntries/const';

export default function Phone(props: {
  setStage: Dispatch<SetStateAction<number>>;
  setCountryCode: Dispatch<SetStateAction<string>>;
  setMobileNumber: Dispatch<SetStateAction<number>>;
  countryCode: string;
  mobileNumber: string;
  appEnv: string;
  setMobileUpdated: Dispatch<SetStateAction<number>>;
  setShowVerify: Dispatch<SetStateAction<boolean>>;
  countdown: number;
  showVerify: boolean;
}) {
  const { t } = useTranslation();
  const { setStage, setMobileUpdated, setShowVerify, countdown, showVerify } = props;
  const { countryCode, setCountryCode, mobileNumber, setMobileNumber, appEnv } = props;
  const [active, setActive] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const showToast = useToast();
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [initialMobileNumber, setInitialMobileNumber] = useState(mobileNumber);

  useEffect(() => {
    // Store the initial mobile number when component mounts
    setInitialMobileNumber(mobileNumber);
  }, []);

  useEffect(() => {
    setActive(mobileNumber?.length > 0 && countryCode?.length > 0);
  }, [mobileNumber, countryCode]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    setMobileNumber(e.target.value);
  };

  const handleSelectCountry = (code: string) => {
    setCountryCode(code);
    setIsOpen(false);
  };

  const handleShowCaptcha = () => {
    if (OpenCAPTCHA && (initialMobileNumber !== mobileNumber || countdown <= 0 || !showVerify)) {
      setShowCaptcha(true);
    } else {
      handleCommit();
    }
  };

  const handleCommit = useCallback(async () => {
    const res = await fetch(`${window.location.protocol}//${window.location.host}/api/confirmation-code`, {
      method: 'POST',
      body: JSON.stringify({
        countryCode: Number(countryCode),
        mobileNumber,
        grantType: 'phone',
        signature: generateSignature('phone', '', String(Number(countryCode)), mobileNumber),
        blackbox: window.__blackbox,
      }),
    });
    if (res.ok) {
      // 检查响应是否成功
      const responseBody = await res.json(); // 解析响应体为 JSON
      if (responseBody.meta.code === 200) {
        if (initialMobileNumber !== mobileNumber || countdown <= 0) {
          setMobileUpdated(new Date().getTime());
        }
        setShowVerify(true);
        return setStage(Stage.Varify);
      } else if (responseBody.meta.code === 40010) {
        showToast(t('DUET_H5_ACCOUNT_MANAGE_PHONE_ERROR_TOAST'));
      } else if (responseBody.meta.code === 40013) {
        showToast(t('DUET_H5_ACCOUNT_MANAGE_REQUEST_MAX_ERROR_TOAST'));
      } else {
        showToast('Send verification code failed');
      }
    }
    setIsLoading(false);
  }, [mobileNumber, countryCode, isLoading]);

  return (
    <>
      <div className={s.icon} />
      <div className={s.title}>{t('DUET_WEB_SIGNIN_NUMBER_TITLE')}</div>
      <div className={s.inputWrapper}>
        <div className={s.countryCodeWrapper}>
          <div>{t('DUET_WEB_SIGNIN_NUMBER_COUNTRYCODE')}</div>
          <div className={s.customSelect} ref={dropdownRef}>
            <div className={cx(s.selectHeader, { [s.open]: isOpen })} onClick={() => setIsOpen(!isOpen)}>
              <div className={s.countryCode}>{countryCode}</div>
              <span className={s.arrow} />
            </div>
            {isOpen && (
              <div className={s.dropdown}>
                {COUNTRY_CODES.map((item) => (
                  <div
                    key={item.code}
                    className={cx(s.option, { [s.selected]: item.code === countryCode })}
                    onClick={() => handleSelectCountry(item.dialCode)}
                  >
                    {`${item.code} `}
                    <span className={s.dialCode}>{item.dialCode}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className={s.mobileWrapper}>
          <div>{t('DUET_WEB_SIGNIN_NUMBER_MOBILE')}</div>
          <input
            type="number"
            value={mobileNumber}
            onChange={handleChange}
            className={s.input}
            placeholder={t('DUET_H5_ACCOUNT_MANAGE_PHONE_LOGIN_NUMBER_PAGE_PLACEHOLDER')}
          />
        </div>
      </div>
      <div className={s.bottomText}>{t('DUET_WEB_SIGNIN_NUMBER_CONTENT')}</div>
      <div className={cx(s.button, { [s.active]: active, [s.loading]: isLoading })} onClick={handleShowCaptcha}>
        {isLoading ? <div className={s.loadingSpinner} /> : t('DUET_WEB_SIGNIN_NUMBER_BUTTON')}
      </div>
      {showCaptcha && <HCaptcha setShowCaptcha={setShowCaptcha} toNext={handleCommit} />}
    </>
  );
}
